import { SxProp, TextInput, View } from 'dripsy';
import React, { useEffect } from 'react';
import { KeyboardTypeOptions, LayoutChangeEvent } from 'react-native';
import { useStore } from '../../../../store/store';
import { Icon, SVGIconList } from '../icons/Icon';
import { TextBody, TextCaption } from '../typography/UniTypography';
import { useInput, UseInputRegularVariant } from './useInput.hook';
import { UniInputVariant, useInputTheme } from './useInputTheme.hook';

enum StyleName {
  container = 'container',
  wrapper = 'wrapper'
}

interface UniInputProps {
  variant?: UniInputVariant;
  size?: 'small' | 'medium' | 'large' | 'default';
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  labelText?: string;
  errorText?: string;
  prefixText?: string;
  suffixText?: string;
  prefixIcon?: SVGIconList;
  suffixIcon?: SVGIconList;
  sx?: SxProp;
  wrapperSx?: SxProp;
  inputSx?: SxProp;
  textAlignVertical?: 'auto' | 'top' | 'bottom' | 'center' | undefined;
  multiline?: boolean | undefined;
  prefixIconStyle?: {
    height: number;
    width: number;
  };
  disabled?: boolean;
  hasFocus?: boolean;
  keyboardType?: KeyboardTypeOptions;
  onValueChange?: (value?: string) => void;
  onLayout?: (event: LayoutChangeEvent) => void;
  regular?: UseInputRegularVariant | RegExp;
  unicodeCharsLength?: number;
}

export const UniInput: React.FC<UniInputProps> = (props) => {
  const {
    sx,
    wrapperSx,
    inputSx,
    textAlignVertical,
    multiline,
    size = 'medium',
    variant,
    labelText,
    errorText,
    value,
    defaultValue,
    placeholder,
    disabled,
    prefixText,
    suffixText,
    prefixIcon,
    suffixIcon,
    prefixIconStyle,
    hasFocus,
    keyboardType,
    onValueChange,
    onLayout,
    regular,
    unicodeCharsLength
  } = props;

  let inputVariant = variant;
  if (!inputVariant) {
    const store = useStore();
    inputVariant = store.inputVariant;
  }

  const input = useInput({ value, defaultValue, onValueChange, regular, unicodeCharsLength });
  const inputTheme = useInputTheme({
    size,
    variant: inputVariant,
    isError: !!errorText,
    disabled
  });

  useEffect(() => {
    if (hasFocus) {
      inputTheme.ref.current?.focus();
    } else {
      inputTheme.ref.current?.blur();
    }
  }, [hasFocus]);

  return (
    <View onLayout={onLayout} sx={{ ...wrapperSx, flex: 1, alignItems: 'flex-start' }}>
      {labelText && (
        <TextCaption semiBold label themePrimary={variant === UniInputVariant.v3 && inputTheme.hasFocus}>
          {labelText}
        </TextCaption>
      )}
      <View sx={{ width: '100%', ...inputTheme.wrapperStyle, ...sx }}>
        {prefixText && (
          <TextBody secondary sx={{ ml: '$3' }}>
            {prefixText}
          </TextBody>
        )}
        {prefixIcon && (
          <View sx={{ ml: '$3' }}>
            <Icon name={prefixIcon} height={prefixIconStyle?.height} width={prefixIconStyle?.width} />
          </View>
        )}
        <TextInput
          ref={inputTheme.ref}
          sx={{ ...inputTheme.inputStyle, ...inputSx }}
          onFocus={inputTheme.onFocus}
          onBlur={() => inputTheme.onBlur()}
          value={input.value}
          onChangeText={input.onChange}
          returnKeyType="done"
          editable={!disabled}
          placeholder={placeholder}
          multiline={multiline}
          textAlignVertical={textAlignVertical}
          keyboardType={keyboardType}
        />
        {suffixText && (
          <TextBody secondary sx={{ mr: '$3' }}>
            {suffixText}
          </TextBody>
        )}
        {suffixIcon && (
          <View sx={{ mr: '$3' }}>
            <Icon name={suffixIcon} />
          </View>
        )}
      </View>
      <ErrorText errorText={errorText} />
    </View>
  );
};

const ErrorText = (props: { errorText?: string }) => {
  const { errorText } = props;
  if (!errorText) return null;

  return (
    <TextCaption
      danger
      sx={{
        mt: '$1'
      }}>
      {errorText}
    </TextCaption>
  );
};
